import * as React from "react";
import { observer, MobXProviderContext } from "mobx-react";
import { FlexComponentNameToWizardStateConfig } from "src/components/shared/StorefrontWizard/FlexComponentNameToWizardStateConfig";
import { LobNavigationProps } from "src/components/shared/StorefrontWizard/LobNavigation";
import { QueryObject, RouteStore } from "bernie-core";
import { Navigation } from "src/stores/wizard/config";
import { ClientWizardStateStore } from "src/stores/wizard/ClientWizardStateStore";
import { UitkLayoutFlex } from "uitk-react-layout-flex";
import { useLocalization } from "@shared-ui/localization-context";
import { scrimValues } from "./typings";
import { LobNavigationScrims } from "./LobNavigationScrims";
import LobNavigationItems from "./LobNavigationItems";
import { withStores } from "stores";
import { UitkSpacing } from "uitk-react-spacing";

export const LobNavigation = withStores("flexModuleModelStore")(
  observer(({ lobs, flexModuleModelStore }: LobNavigationProps) => {
    const wizardState: ClientWizardStateStore = React.useContext(MobXProviderContext).wizardState;
    const routeStore: RouteStore = React.useContext(MobXProviderContext).routeStore;
    const defaultLobNavConfig: Navigation = {
      tabIcon: "help",
      tabTitleToken: "",
    };

    // No need for navigation in a single LOB pages
    if (lobs.length === 1) {
      return null;
    }

    // Check the route store, if there is no LOB query attached, get it from the wizardState default and redirect
    if (!routeStore.queryParams.pwaLob) {
      const { useDefaultLOB, defaultLob: pwaLob } = wizardState;

      if (useDefaultLOB) {
        routeStore.updateSearch({ pwaLob });
        wizardState.setUseDefaultLOB(false);
      } else if (typeof window !== "undefined" && window.history) {
        window.history.back();
      }
    }

    const lobQueryCombiner = (prevQuery: QueryObject, nextQuery: QueryObject): QueryObject => {
      delete prevQuery.pwaSubLOB;

      return { ...prevQuery, ...nextQuery };
    };

    const handleLobChange = (lobName: string, options: any) => () => {
      const { directLink } = options;

      if (directLink) {
        window.location.assign(directLink);

        return;
      }

      let queryParams: any = { pwaLob: lobName };
      const { subLOBState } = wizardState.lobStateMap[lobName];

      if (subLOBState) {
        queryParams = { pwaLob: lobName, pwaSubLOB: subLOBState };
      }
      routeStore.updateSearch(queryParams, {}, false, lobQueryCombiner);
    };

    const isTabChecked = (lobName: string): boolean => wizardState.activeLob === lobName;
    const { formatText } = useLocalization();

    const getLobIcon = (lobName: string): string => {
      // If there is no config for the lob it will use help icon as default
      let lobIcon = defaultLobNavConfig.tabIcon;
      if (wizardState[FlexComponentNameToWizardStateConfig[lobName]]) {
        lobIcon = wizardState[FlexComponentNameToWizardStateConfig[lobName]].config.navigation.tabIcon;
      }

      return lobIcon;
    };

    const getLabel = (lobName: string): string => {
      let label = defaultLobNavConfig.tabTitleToken;
      if (wizardState[FlexComponentNameToWizardStateConfig[lobName]]) {
        label = formatText(wizardState[FlexComponentNameToWizardStateConfig[lobName]].config.navigation.tabTitleToken);
      }

      return label;
    };

    const [scrimsToDisplay, setScrimToDisplay] = React.useState(scrimValues.RIGHT);

    const onLobNavigationScroll = (event: React.UIEvent<HTMLUListElement>) => {
      const scrollLeftMin = 0;
      const { scrollLeft, offsetWidth, scrollWidth } = event.currentTarget;

      const displayLeft = scrollLeft > scrollLeftMin;
      const displayRight = scrollLeft + offsetWidth < scrollWidth;

      let currentScrims = scrimValues.RIGHT;

      if (displayLeft && displayRight) {
        currentScrims = scrimValues.BOTH;
      } else if (displayLeft) {
        currentScrims = scrimValues.LEFT;
      }

      setScrimToDisplay(currentScrims);
    };

    return (
      <UitkSpacing margin={{ small: { blockend: "one" } }}>
        <div className="lobNavigationWrapper">
          <UitkLayoutFlex
            tag="ul"
            className="lobNavigation no-bullet uitk-scroll-horizontal"
            onScroll={onLobNavigationScroll}
            data-testid="lob-navigation"
            role="tablist"
          >
            <LobNavigationItems
              lobs={lobs}
              flexModuleModelStore={flexModuleModelStore}
              getLabel={getLabel}
              isTabChecked={isTabChecked}
              getLobIcon={getLobIcon}
              handleLobChange={handleLobChange}
              searchParams={routeStore.search}
            />
          </UitkLayoutFlex>

          <LobNavigationScrims scrimsToDisplay={scrimsToDisplay} />
        </div>
      </UitkSpacing>
    );
  })
);
